import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { consultation } from "../../types/index";

const initialState: consultation.ConsultationStateType = {
  upcomingConsultations: {
    isLoading: false,
    data: null,
    errors: null,
  },
  pastConsultations: {
    isLoading: false,
    data: null,
    errors: null,
  },
};

const consultationSlice = createSlice({
  name: "consultation",
  initialState,
  reducers: {
    // Upcoming — one call, carries the summary for the whole history.
    getUpcomingConsultationsRequest: (
      state: consultation.ConsultationStateType,
      action: PayloadAction<any>,
    ) => {
      state.upcomingConsultations.isLoading = true;
      state.upcomingConsultations.errors = null;
    },
    getUpcomingConsultationsSuccess: (
      state: consultation.ConsultationStateType,
      action: PayloadAction<any>,
    ) => {
      state.upcomingConsultations.isLoading = false;
      state.upcomingConsultations.data = action.payload.data;
      state.upcomingConsultations.errors = null;
    },
    getUpcomingConsultationsError: (
      state: consultation.ConsultationStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.upcomingConsultations.isLoading = false;
      state.upcomingConsultations.data = null;
      state.upcomingConsultations.errors = error;
    },

    // Past — paged, so the previous page is left in place while the next one
    // loads rather than blanking the list.
    getPastConsultationsRequest: (
      state: consultation.ConsultationStateType,
      action: PayloadAction<any>,
    ) => {
      state.pastConsultations.isLoading = true;
      state.pastConsultations.errors = null;
    },
    getPastConsultationsSuccess: (
      state: consultation.ConsultationStateType,
      action: PayloadAction<any>,
    ) => {
      const incoming = action.payload.data;
      const pageNo = incoming?.list?.pagination?.pageNo ?? 1;
      const incomingRows = incoming?.list?.rows || [];
      const previousRows = state.pastConsultations.data?.list?.rows;

      // A page that comes back empty is the end, whatever hasMore claims -
      // otherwise the observer would keep asking for pages that never arrive.
      const pagination = incoming?.list?.pagination
        ? {
            ...incoming.list.pagination,
            hasMore: incomingRows.length
              ? incoming.list.pagination.hasMore
              : false,
          }
        : incoming?.list?.pagination;

      state.pastConsultations.isLoading = false;
      state.pastConsultations.errors = null;
      // Page 2 onwards appends, so scrolling extends the list rather than
      // replacing it. Page 1 always replaces, so a category change starts over.
      state.pastConsultations.data = {
        ...incoming,
        list: {
          ...incoming?.list,
          pagination,
          rows:
            pageNo > 1 && previousRows
              ? [...previousRows, ...incomingRows]
              : incomingRows,
        },
      };
    },
    getPastConsultationsError: (
      state: consultation.ConsultationStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.pastConsultations.isLoading = false;
      // Rows already on screen stay: a page that fails to load is no reason to
      // take away the ones the coach is reading.
      state.pastConsultations.errors = error;
    },

    resetConsultations: (state: consultation.ConsultationStateType) => {
      state.upcomingConsultations = {
        isLoading: false,
        data: null,
        errors: null,
      };
      state.pastConsultations = { isLoading: false, data: null, errors: null };
    },
  },
});

export const {
  getUpcomingConsultationsRequest,
  getUpcomingConsultationsSuccess,
  getUpcomingConsultationsError,

  getPastConsultationsRequest,
  getPastConsultationsSuccess,
  getPastConsultationsError,

  resetConsultations,
} = consultationSlice.actions;

export default consultationSlice.reducer;
